import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { BankAccountCard, CopyInput, Loading, PageHeader, RenderFields, SectionHeader } from '~/components';
import { AchBatchRepository } from '~/repositories/Transfer/AchBatchRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { ACHTransferBatch } from '~/typings/API';

import { AchBatchHeader } from './_components/AchBatchHeader';

const POLL_MS = 5000;

const NON_TERMINAL_BATCH = new Set(['pending', 'validating', 'validated', 'processing']);

const STATEMENT_GROUPING_LABEL: Record<string, string> = {
  individual: 'Individual',
  rolled_up: 'Rolled up',
};

interface Params {
  id: string;
}

export const PageTransfersAchBatchView: React.FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  const formMethods = useForm();

  const { data: batch, isLoading: isBatchLoading } = useQuery<ACHTransferBatch>({
    queryKey: ['ach-batch', platformId, isSandbox, id],
    queryFn: () => AchBatchRepository.get({ id }),
    enabled: !!id,
    refetchInterval: ({ state }) => (NON_TERMINAL_BATCH.has(state.data?.status ?? '') ? POLL_MS : false),
  });

  const bankAccountId = batch?.importConfig?.nacha?.bankAccountId;

  return (
    <>
      <PageHeader text="ACH Batch" borderOnScroll />
      <Loading isLoading={isBatchLoading} fullHeight>
        {batch && (
          <>
            <AchBatchHeader batch={batch} />
            <SectionHeader text="Batch Details" border />
            <Inner pt={0} px="var(--page-margin)">
              <FormProvider {...formMethods}>
                <RenderFields
                  sections={[
                    {
                      variant: 'isCompact',
                      fields: [
                        {
                          id: 'description',
                          label: 'Description',
                          children: () => <CopyInput value={batch.description || '-'} />,
                        },
                        {
                          id: 'importType',
                          label: 'Import Type',
                          children: () => <CopyInput value={batch.importType ? batch.importType.toUpperCase() : '-'} />,
                        },
                        ...(bankAccountId
                          ? [
                              {
                                id: 'bankAccount',
                                label: 'Bank Account',
                                children: () => <BankAccountCard bankAccountId={bankAccountId} />,
                              },
                            ]
                          : []),
                        {
                          id: 'statementGroupingMode',
                          label: 'Statement Grouping',
                          children: () => (
                            <CopyInput
                              value={
                                STATEMENT_GROUPING_LABEL[batch.statementGroupingMode] ?? batch.statementGroupingMode
                              }
                            />
                          ),
                        },
                      ],
                    },
                  ]}
                />
              </FormProvider>
            </Inner>
          </>
        )}
      </Loading>
    </>
  );
};
